<template>
  <div class="message-container" :class="{ 'own-message': isOwnMessage }">
    <div class="message-bubble" :class="messageClasses">
      <!-- Reply to message -->
      <div v-if="message.replyTo" class="reply-container" @click="scrollToReply">
        <div class="reply-line"></div>
        <div class="reply-content">
          <div class="reply-sender">{{ message.replyTo.sender.Name }}</div>
          <div class="reply-text">
            <span v-if="message.replyTo.type === 'image'">📷 Image</span>
            <span v-else-if="message.replyTo.type === 'video'">🎥 Video</span>
            <span v-else-if="message.replyTo.type === 'audio'">🎵 Audio</span>
            <span v-else-if="message.replyTo.type === 'document'">📄 Document</span>
            <span v-else>{{ message.replyTo.content }}</span>
          </div>
        </div>
      </div>

      <!-- Forwarded message indicator -->
      <div v-if="message.forwarded" class="forwarded-indicator">
        <v-icon small class="mr-1">mdi-forward</v-icon>
        Forwarded
      </div>

      <!-- Message content -->
      <div class="message-content">
        <!-- Text message -->
        <div v-if="message.type === 'text'" class="text-content">
          {{ message.content }}
        </div>

        <!-- Image message -->
        <div v-else-if="message.type === 'image'" class="image-content">
          <v-img
            :src="getFileUrl(message.fileUrl)"
            :alt="message.fileName"
            max-width="300"
            max-height="300"
            @click="openImageViewer"
            class="message-image"
          ></v-img>
          <div v-if="message.content" class="image-caption">
            {{ message.content }}
          </div>
        </div>

        <!-- Video message -->
        <div v-else-if="message.type === 'video'" class="video-content">
          <video
            :src="getFileUrl(message.fileUrl)"
            controls
            preload="metadata"
            class="message-video"
            max-width="300"
            max-height="300"
          ></video>
          <div v-if="message.content" class="video-caption">
            {{ message.content }}
          </div>
        </div>

        <!-- Audio message -->
        <div v-else-if="message.type === 'audio'" class="audio-content">
          <div class="audio-player">
            <v-icon class="mr-2">mdi-play-circle</v-icon>
            <audio
              :src="getFileUrl(message.fileUrl)"
              controls
              preload="metadata"
              class="message-audio"
            ></audio>
          </div>
          <div v-if="message.content" class="audio-caption">
            {{ message.content }}
          </div>
        </div>

        <!-- Document message -->
        <div v-else-if="message.type === 'document'" class="document-content">
          <div class="document-preview" @click="downloadFile">
            <v-icon large class="mr-3">mdi-file-document</v-icon>
            <div class="document-info">
              <div class="document-name">{{ message.fileName }}</div>
              <div class="document-size">{{ formatFileSize(message.fileSize) }}</div>
            </div>
            <v-icon>mdi-download</v-icon>
          </div>
          <div v-if="message.content" class="document-caption">
            {{ message.content }}
          </div>
        </div>

        <!-- Location message -->
        <div v-else-if="message.type === 'location'" class="location-content">
          <div class="location-preview" @click="openLocation">
            <v-icon large class="mr-3">mdi-map-marker</v-icon>
            <div class="location-info">
              <div class="location-address">{{ message.location.address }}</div>
              <div class="location-coords">
                {{ message.location.latitude.toFixed(6) }}, {{ message.location.longitude.toFixed(6) }}
              </div>
            </div>
            <v-icon>mdi-open-in-new</v-icon>
          </div>
        </div>
      </div>

      <!-- Message metadata -->
      <div class="message-meta">
        <span class="message-time">{{ formatTime(message.createdAt) }}</span>
        
        <!-- Message status indicators -->
        <div class="message-status">
          <v-icon v-if="message.isRead" small color="blue">mdi-check-all</v-icon>
          <v-icon v-else small color="grey">mdi-check</v-icon>
        </div>
      </div>
    </div>

    <!-- Message actions menu -->
    <v-menu v-model="showMenu" :activator="null" absolute>
      <v-list dense>
        <v-list-item @click="replyToMessage">
          <v-list-item-icon>
            <v-icon small>mdi-reply</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Reply</v-list-item-title>
        </v-list-item>
        
        <v-list-item @click="forwardMessage">
          <v-list-item-icon>
            <v-icon small>mdi-forward</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Forward</v-list-item-title>
        </v-list-item>
        
        <v-list-item v-if="isOwnMessage" @click="deleteMessage">
          <v-list-item-icon>
            <v-icon small color="red">mdi-delete</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="red--text">Delete</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Image viewer dialog -->
    <v-dialog v-model="showImageViewer" max-width="90vw">
      <v-card>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn icon @click="showImageViewer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
        <v-card-text class="text-center">
          <v-img
            :src="getFileUrl(message.fileUrl)"
            :alt="message.fileName"
            max-height="80vh"
            contain
          ></v-img>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

const url = require("@/client_config/config").url;

export default {
  name: 'ChatMessage',
  props: {
    message: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showMenu: false,
      showImageViewer: false
    };
  },
  computed: {
    isOwnMessage() {
      // Handle both populated and unpopulated sender objects
      const senderId = this.message.sender._id || this.message.sender;
      
      // Convert both IDs to strings for comparison to handle ObjectId vs String differences
      const senderIdStr = String(senderId);
      const currentUserIdStr = String(this.currentUserId);
      const isOwn = senderIdStr === currentUserIdStr;
      
      // Debug logging
      console.log('Message alignment check:', {
        messageId: this.message._id,
        senderId: senderId,
        senderIdStr: senderIdStr,
        currentUserId: this.currentUserId,
        currentUserIdStr: currentUserIdStr,
        isOwn: isOwn,
        senderType: typeof this.message.sender,
        senderHasId: !!this.message.sender._id
      });
      
      return isOwn;
    },
    messageClasses() {
      return {
        'own-message-bubble': this.isOwnMessage,
        'other-message-bubble': !this.isOwnMessage,
        'has-reply': this.message.replyTo,
        'is-forwarded': this.message.forwarded
      };
    }
  },
  methods: {
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    formatFileSize(bytes) {
      if (!bytes) return '';
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    },

    getFileUrl(fileUrl) {
      if (!fileUrl) return '';
      // If it's already a full URL (AWS S3), return as is
      if (fileUrl.startsWith('http')) return fileUrl;
      // Fallback for local files (if any)
      return `${url}${fileUrl}`;
    },

    scrollToReply() {
      // Emit event to scroll to replied message
      this.$emit('scroll-to-reply', this.message.replyTo._id);
    },

    openImageViewer() {
      this.showImageViewer = true;
    },

    async downloadFile() {
      try {
        const response = await axios.get(this.getFileUrl(this.message.fileUrl), {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.message.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    },

    openLocation() {
      const { latitude, longitude } = this.message.location;
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, '_blank');
    },

    replyToMessage() {
      this.$emit('reply', this.message);
      this.showMenu = false;
    },

    forwardMessage() {
      this.$emit('forward', this.message);
      this.showMenu = false;
    },

    deleteMessage() {
      this.$emit('delete', this.message);
      this.showMenu = false;
    },

    showMessageMenu(event) {
      event.preventDefault();
      this.showMenu = true;
    }
  }
};
</script>

<style scoped>
.message-container {
  display: flex;
  margin-bottom: 8px;
  position: relative;
}

.message-container.own-message {
  justify-content: flex-end;
}

.message-bubble {
  max-width: 85%;
  min-width: 120px;
  padding: 8px 12px;
  border-radius: 18px;
  position: relative;
  cursor: pointer;
}

.own-message-bubble {
  background-color: #ffffff;
  color: #000;
  border-bottom-right-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.other-message-bubble {
  background-color: #dcf8c6;
  color: #000;
  border-bottom-left-radius: 4px;
}

.message-bubble:hover {
  opacity: 0.9;
}

.reply-container {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.reply-line {
  width: 3px;
  height: 100%;
  background-color: #075e54;
  margin-right: 8px;
  border-radius: 2px;
}

.reply-content {
  flex: 1;
}

.reply-sender {
  font-size: 0.75rem;
  font-weight: 500;
  color: #075e54;
  margin-bottom: 2px;
}

.reply-text {
  font-size: 0.875rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.forwarded-indicator {
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.message-content {
  word-wrap: break-word;
}

.text-content {
  line-height: 1.4;
}

.image-content,
.video-content,
.audio-content,
.document-content,
.location-content {
  margin-top: 4px;
}

.message-image {
  border-radius: 8px;
  cursor: pointer;
}

.message-video {
  border-radius: 8px;
  max-width: 100%;
}

.audio-player {
  display: flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 8px;
  border-radius: 8px;
}

.message-audio {
  flex: 1;
}

.document-preview {
  display: flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.document-preview:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.document-info {
  flex: 1;
  margin: 0 12px;
}

.document-name {
  font-weight: 500;
  margin-bottom: 2px;
}

.document-size {
  font-size: 0.75rem;
  color: #666;
}

.location-preview {
  display: flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.location-preview:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.location-info {
  flex: 1;
  margin: 0 12px;
}

.location-address {
  font-weight: 500;
  margin-bottom: 2px;
}

.location-coords {
  font-size: 0.75rem;
  color: #666;
}

.image-caption,
.video-caption,
.audio-caption,
.document-caption {
  margin-top: 8px;
  font-size: 0.875rem;
  color: #666;
}

.message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  gap: 4px;
}

.message-time {
  font-size: 0.75rem;
  color: #666;
}

.message-status {
  display: flex;
  align-items: center;
}

@media (max-width: 600px) {
  .message-bubble {
    max-width: 90%;
  }
}
</style> 